import type { FC } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "~/shared/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/shared/ui/select";
import { cn } from "~/shared/lib/utils";

interface DataTablePaginationProps {
    pageIndex: number;
    pageSize: number;
    total: number;
    pageSizeOptions?: number[];
    onPaginationChange: (pageIndex: number, pageSize: number) => void;
    /**
     * Value shown in the rows-per-page selector. Defaults to `pageSize`. Pass the user's
     * TARGET page size here when `pageSize` is an effective (column-rounded) size, so the
     * selector keeps showing the chosen number while pagination counts the rows fetched.
     */
    selectorPageSize?: number;
    showingLabel?: string;
    rowsPerPageLabel?: string;
    /** Outer padding/layout override. Lets each table system align the bar with its own gutter. */
    className?: string;
}

const DataTablePagination: FC<DataTablePaginationProps> = ({
    pageIndex,
    pageSize,
    total,
    pageSizeOptions = [5, 10, 25, 50, 100],
    onPaginationChange,
    selectorPageSize,
    showingLabel = "Showing {{from}}\u2013{{to}} of {{total}}",
    rowsPerPageLabel = "Rows per page",
    className = "px-2 py-2",
}) => {
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const from = total === 0 ? 0 : pageIndex * pageSize + 1;
    const to = Math.min((pageIndex + 1) * pageSize, total);

    // The selector shows the user's target; pagination math above uses the effective
    // (column-rounded) pageSize.
    const selectValue = selectorPageSize ?? pageSize;

    // Always keep the active value selectable so the trigger never renders empty
    // (e.g. a default of 20 against the standard [5,10,25,50,100] options).
    const resolvedOptions = pageSizeOptions.includes(selectValue)
        ? pageSizeOptions
        : [...pageSizeOptions, selectValue].sort((a, b) => a - b);

    const formattedShowing = showingLabel
        .replace("{{from}}", String(from))
        .replace("{{to}}", String(to))
        .replace("{{total}}", String(total));

    return (
        <div className={cn("flex items-center justify-between gap-4", className)}>
            <div className="text-sm text-muted-foreground">{formattedShowing}</div>

            <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                    <span className="text-sm whitespace-nowrap">{rowsPerPageLabel}</span>
                    <Select
                        value={String(selectValue)}
                        onValueChange={(v) => onPaginationChange(0, Number(v))}
                    >
                        <SelectTrigger
                            size="sm"
                            className="w-[76px] focus-visible:border-input focus-visible:ring-0"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent position="popper" side="top">
                            {resolvedOptions.map((size) => (
                                <SelectItem key={size} value={String(size)}>
                                    {size}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center gap-1">
                    <Button
                        variant="outline"
                        size="icon"
                        className="size-8"
                        onClick={() => onPaginationChange(0, pageSize)}
                        disabled={pageIndex === 0}
                    >
                        <ChevronsLeft className="size-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="size-8"
                        onClick={() => onPaginationChange(pageIndex - 1, pageSize)}
                        disabled={pageIndex === 0}
                    >
                        <ChevronLeft className="size-4" />
                    </Button>
                    <span className="px-2 text-sm whitespace-nowrap">
                        {pageIndex + 1} / {pageCount}
                    </span>
                    <Button
                        variant="outline"
                        size="icon"
                        className="size-8"
                        onClick={() => onPaginationChange(pageIndex + 1, pageSize)}
                        disabled={pageIndex >= pageCount - 1}
                    >
                        <ChevronRight className="size-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="size-8"
                        onClick={() => onPaginationChange(pageCount - 1, pageSize)}
                        disabled={pageIndex >= pageCount - 1}
                    >
                        <ChevronsRight className="size-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DataTablePagination;
